import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import GenericBasicInfo from "../../../../../components/cards/GenericBasicInfo";
import {
  cardStyles,
  modalGenericStyles,
  variables,
} from "../../../../../variables";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  retrieveAdhocReservation,
  patchAddhocReservation,
} from "../../../../../api/CRS/Adhoc";
import { DateTime } from "luxon";
import GuestCard from "../../../../../components/cards/GuestInfo";
import useReservationDetails from "../../../../../hooks";
import EditGuestModal from "../../../CommonCRSModals/EditGuestModal";
import EmailsCard from "../../../../../components/cards/emails";
import Loader from "../../../../../components/Loader";
import EditCRSTagModal from "../../../CommonCRSModals/EditCRSTagModal";
import { toast } from "react-toastify";
import { Price } from "../../../../../components/Price";
import ReservationEventsModal from "../../../ReservationEventsModal";
import { DocumentsCRSCard } from "../../../CommonCRSModals/DocumentsCRS/List";
import ReservationsStatusTag from "../../../../../components/Tags/ReservationsStatusTag";
import TabStrip from "../../../../../components/TabStrip";
import { CustomButton } from "../../../../../components/CustomButton";
import AdhocLogsTab from "./AdhocLogsTab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { genericRequestErrorHandler } from "../../../../../api";
import Receivables from "../../../CommonCRSModals/Receivables";
import { InternalLogs } from "../../../CommonCRSModals/InternalLogs";

const styles = createUseStyles({
  ...modalGenericStyles,
  DetailAdhocModal: { ...modalGenericStyles.modal },
  modalCard: {
    ...modalGenericStyles.card,
    gridTemplateRows: "max-content max-content 1fr max-content",
  },
  modalCardHeader: {
    ...modalGenericStyles.cardHeader,
  },
  modalCardBody: {
    ...modalGenericStyles.cardBody,
    display: "grid",
    padding: "",
    overflowX: "hidden",
  },
  modalCardActions: {
    ...modalGenericStyles.cardActions,
  },
  card: { ...cardStyles.card, maxWidth: "100%" },
  cardHeader: cardStyles.header,
  cardBody: {
    ...cardStyles.body,
    gap: variables.normal_gap,
    maxWidth: "100%",
    overflow: "auto",
    justifyContent: "left",
  },
  firstRow: {
    display: "grid",
    gridTemplateColumns: "repeat(1, 1fr)",
    padding: variables.normal_gap,
    gap: variables.normal_gap,
  },
  creatorCardBody: {
    extend: "cardBody",
    padding: [`calc(${variables.normal_gap} * 3.3)`, variables.normal_gap],
  },
  componentRow: {
    extend: "firstRow",
    gridTemplateColumns: "repeat(1, 1fr)",
  },
  [`@media ${variables.media.smallscreen}`]: {
    modalCardBody: {
      minWidth: "60rem",
    },
    firstRow: {
      gridTemplateColumns: "1fr 1fr",
      justifyItems: "center",
    },
  },
});

const DetailAdhocModal = ({ reference, onClose }) => {
  const classes = styles();

  const [activeTab, setActiveTab] = useState("basicInfo");

  const queryClient = useQueryClient();

  const menuTabs = {
    basicInfo: "Basic Info",
    reservationEvents: "Reservation Events",
    receivables: "Receivables",
    documents: "Documents",
    internalLogs: "Internal Logs",
    logs: "User Action Logs",
  };

  const {
    data: adhoc,
    guestEditMode,
    guestEditId,
    editCRSTagMode,
    reservationEventsModal,
    openGuestModal,
    closeGuestModal,
    openEditCRSTagModal,
    closeEditCRSTagModal,
    closeReservationEventsModal,
    // loading,
  } = useReservationDetails({
    reference,
    retrieveReservationData: retrieveAdhocReservation,
    onCloseDetails: onClose,
  });

  const basicDataCpls = [
    ["Reference", "reference"],
    ["Supplier Reference", "supplier_reference"],
    ["Tags", null, _.get(adhoc, "tags", []).join(",")],
    ["Destination", "destination_name"],
    [
      "Check In",
      null,
      DateTime.fromISO(_.get(adhoc, "data.check_in")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    [
      "Check Out",
      null,
      DateTime.fromISO(_.get(adhoc, "data.check_out")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    ["Adults", "adults"],
    ["Children", null, _.get(adhoc, "data.children") || "0"],
    ["Payment Mode", "payment_mode_display"],
    ["Market Type", "market_type"],
    ["Source Entity", "source_entity"],
    [
      "Original Service Price",
      null,
      <Price
        currency={_.get(adhoc, "data.currency")}
        price={_.get(adhoc, "data.original_service_price")}
      />,
    ],
    [
      "Created",
      null,
      DateTime.fromISO(_.get(adhoc, "data.created")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    [
      "Status",
      null,
      <ReservationsStatusTag status={_.get(adhoc, "data.status")} />,
    ],
  ];
  const creatorDataCpl = [
    ["External Creator", "external_creator"],
    [
      "Creator",
      null,
      _.get(adhoc, "data.guests", []).map((des) => _.get(des, "creator")),
    ],
  ];

  const { mutate: onSubmitTags, isLoading: tagsLoading } = useMutation({
    mutationFn: (values) => {
      return patchAddhocReservation({ payload: values, reference });
    },
    onSuccess: () => {
      toast.success("Tags updated successfully");
      queryClient.invalidateQueries("adhocs");
      onClose();
    },
    onError: (error) => {
      genericRequestErrorHandler(error);
    },
  });

  return (
    <div id="DetailADModal" className={classes.DetailAdhocModal}>
      <div className={classes.modalCard}>
        <div className={classes.modalCardHeader}>
          <span>Detail Adhoc</span>
        </div>
        <TabStrip
          tabs={Object.entries(menuTabs)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <div className={classes.modalCardBody}>
          {_.isEmpty(adhoc) ? (
            <Loader />
          ) : (
            <React.Fragment>
              {activeTab === "basicInfo" && (
                <React.Fragment>
                  <div className={classes.firstRow}>
                    <div className={classes.cardBody}>
                      <GenericBasicInfo
                        actions={[["Edit", () => openEditCRSTagModal()]]}
                        header={"Basic Data"}
                        cardInfo={basicDataCpls}
                        objectData={_.get(adhoc, "data")}
                      />
                    </div>
                    <div className={classes.creatorCardBody}>
                      <GenericBasicInfo
                        header="Creator Data"
                        cardInfo={creatorDataCpl}
                        objectData={_.get(adhoc, "data")}
                      />
                    </div>
                  </div>
                  <div className={classes.componentRow}>
                    <div className={classes.cardBody}>
                      <GuestCard
                        title={"Guest Informations"}
                        guests={_.get(adhoc, "data.guests", [])}
                        onEditGuest={(id) => {
                          openGuestModal(id);
                        }}
                        onCloseEdit={() => closeGuestModal()}
                      />
                      <EmailsCard
                        title={"Email Informations"}
                        market_type={_.get(adhoc, "data.market_type")}
                        reservation_id={_.get(adhoc, "data.reference")}
                        service_type="ACC"
                        status={_.get(adhoc, "data.status")}
                      />
                    </div>
                  </div>
                </React.Fragment>
              )}
              {activeTab === "reservationEvents" && (
                <ReservationEventsModal
                  header="Reservation Events"
                  onClose={() => closeReservationEventsModal()}
                  events={_.get(adhoc, "data.reservation_events", [])}
                />
              )}
              {guestEditMode && !reservationEventsModal && !editCRSTagMode && (
                <EditGuestModal
                  onClose={closeGuestModal}
                  guestId={guestEditId}
                  queryKey={`accommodation_reservation_users_documents`}
                  reference={_.get(adhoc, "data.reference")}
                  sourceEntity={_.get(adhoc, "data.source_entity", "")}
                />
              )}
              {!guestEditMode && !reservationEventsModal && editCRSTagMode && (
                <EditCRSTagModal
                  header={"Adhoc"}
                  tagsLoading={tagsLoading}
                  tags={_.get(adhoc, "data.tags")}
                  onClose={closeEditCRSTagModal}
                  onSubmitTags={onSubmitTags}
                />
              )}
              {activeTab === "receivables" && (
                <Receivables data={_.get(adhoc, "data.payment_links", [])} />
              )}
              {activeTab === "documents" && (
                <DocumentsCRSCard
                  title="Documents"
                  queryKey={`adhocAccommodation_reservation_document`}
                  showAddButton={true}
                  tags={[`${_.get(adhoc, "data.refernce")}_reservation_ACC`]}
                  reference={_.get(adhoc, "data.refernce")}
                  sourceEntity={_.get(adhoc, "data.source_entity", "")}
                />
              )}
              {activeTab === "internalLogs" && (
                <InternalLogs
                  prebookData={_.get(adhoc, "data.prebook_data", {})}
                  bookData={_.get(adhoc, "data.post_booking_data", {})}
                />
              )}
              {activeTab === "logs" && <AdhocLogsTab />}
            </React.Fragment>
          )}
        </div>
        <div className={classes.modalCardActions}>
          <CustomButton id="cancel" appearance="ghost" onClick={onClose}>
            <strong>Cancel</strong>
          </CustomButton>
        </div>
      </div>
    </div>
  );
};
DetailAdhocModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};
export default DetailAdhocModal;
